import { StrandTimeSeriesData } from "@/pages/caster/models/StrandSpeed";
import { Addition, Charge } from "@/pages/heat-report/models/HeatReportModels";
import { getMomentTz } from "@dexteel/mesf-core";
import { EChartsOption } from "echarts-for-react";
import { useMemo } from "react";

// Color constants
const COLORS = {
	GREEN_BLUE: "#1f77b4", // Blue for KWH/Ton
	ORANGE: "#ff7f0e", // Orange for Power
	WAGENINGEN_GREEN: "#2ca02c", // Green for Additions
	AMARANTH_RED: "#d62728", // Red for Scrap Charges
	MIDDLE_BLUE_PURPLE: "#9467bd", // Purple for Probe Temp
} as const;

const moment = getMomentTz();

interface UseChartOptionsProps {
	eafValues: StrandTimeSeriesData[] | null | undefined;
	heatName: string;
	eafProcessData?: {
		Start: Date;
		End: Date | null;
	} | null;
	charges?: Charge[] | null;
	additions?: Addition[] | null;
	offsetMinutes?: number;
}

const strandColors: { [key: string]: string } = {
	KWH_Per_Ton: COLORS.GREEN_BLUE, // Blue for KWH/Ton
	Power_MW: COLORS.ORANGE, // Orange for Power
	Steel_Temp: COLORS.MIDDLE_BLUE_PURPLE, // Purple for Probe Temp
};

// Define which axis each tag should use
const tagAxisMapping: { [key: string]: number } = {
	KWH_Per_Ton: 0,
	Power_MW: 1,
	Steel_Temp: 2,
};

// Define display names for tags
const tagDisplayNames: { [key: string]: string } = {
	KWH_Per_Ton: "KWH/Ton",
	Power_MW: "Power",
	Steel_Temp: "Probe temp",
};

export const useChartOptions = ({
	eafValues,
	heatName,
	eafProcessData,
	charges,
	additions,
	offsetMinutes = 40,
}: UseChartOptionsProps): EChartsOption => {
	const chartOptions = useMemo<EChartsOption>(() => {
		const data: StrandTimeSeriesData[] = (eafValues || [])
			.map((temp) => ({
				Datetime: temp.Datetime,
				Value: temp.Value ?? 0,
				TagId: temp.TagId?.toString() || "",
				TagName: temp.TagName || "",
			}))
			.filter((item) => item.Datetime && item.TagId && item.TagName);

		if (data.length === 0) {
			return {
				title: {
					left: "center",
					top: "center",
				},
				series: [],
			};
		}

		const groupedData = data.reduce<{ [key: string]: StrandTimeSeriesData[] }>(
			(acc, item) => {
				if (!acc[item.TagName]) {
					acc[item.TagName] = [];
				}
				acc[item.TagName].push(item);
				return acc;
			},
			{},
		);

		// Fixed scales for normalized display (no autoscale)
		const getFixedAxisLimits = (tagName: string) => {
			switch (tagName) {
				case "KWH_Per_Ton":
					return { min: 0, max: 600, interval: 120 };
				case "Power_MW":
					return { min: 0, max: 23000, interval: 4600 };
				case "Steel_Temp":
					// Extended scale to accommodate vertical bands
					// 0-60%: Extra space to avoid overlap with charges/additions
					// 60-100%: Original temperature range (2600-3600°F)
					return { min: 0, max: 3600, interval: 720 };
				default:
					return { min: 0, max: 100, interval: 20 };
			}
		};

		// Calculate min/max for each tag type (autoscale - kept for future flexibility)
		const calculateAxisLimits = (tagName: string) => {
			const tagData = groupedData[tagName] || [];
			if (tagData.length === 0) return { min: 0, max: 100, interval: 20 };

			const values = tagData.map((item) => item.Value);
			const minValue = Math.min(...values);
			const maxValue = Math.max(...values);

			const margin = Math.ceil((maxValue - minValue) * 0.05) || 1;
			const yMin = Math.max(0, Math.floor(minValue) - margin);
			const yMax = Math.ceil(maxValue + margin);
			const interval = Math.ceil((yMax - yMin) / 5) || 1;

			return { min: yMin, max: yMax, interval };
		};

		// Choose scaling method: getFixedAxisLimits for normalized scales, calculateAxisLimits for autoscale
		const getAxisLimits = getFixedAxisLimits;

		// Band mapping functions for vertical distribution
		const mapToBand = (
			value: number,
			minBand: number,
			maxBand: number,
			minValue: number,
			maxValue: number,
		): number => {
			// Map value from original range to band range
			const percentage = (value - minValue) / (maxValue - minValue);
			return minBand + percentage * (maxBand - minBand);
		};

		// Map temperature to 60-100% band (2600-3600°F maps to 60-100% of 0-3600 scale)
		const mapTemperatureToBand = (temp: number): number => {
			return mapToBand(temp, 2160, 3600, 2600, 3600); // 60% of 3600 = 2160
		};

		// Map scrap charges to 0-10% band with alternating min/max positioning
		const mapScrapChargeToBand = (index: number, total: number): number => {
			const bandMin = 0;
			const bandMax = 0.2 * 3600; // 20% of 3600 = 720
			// Alternate between min and max positions
			return index % 2 === 0 ? bandMin : bandMax;
		};

		// Map additions to 10-60% band with vertical staggering
		const mapAdditionToBand = (index: number, total: number): number => {
			const bandMin = 0.2 * 3600; // 20% of 3600 = 720
			const bandMax = 0.6 * 3600; // 60% of 3600 = 2160
			const step = (bandMax - bandMin) / Math.max(total, 1);
			return bandMin + index * step;
		};

		// Filter Probe temp data to show only when temperature value actually changes
		const filterProbeTempEvents = (
			probeData: StrandTimeSeriesData[],
		): StrandTimeSeriesData[] => {
			if (probeData.length === 0) return [];

			const temperatureThreshold = 5; // Minimum change in °F to consider significant
			const filteredData: StrandTimeSeriesData[] = [];

			// Always include the first point
			filteredData.push(probeData[0]);
			let lastIncludedValue = probeData[0].Value;

			// Filter by temperature changes
			for (let i = 1; i < probeData.length; i++) {
				const currentData = probeData[i];
				const currentValue = currentData.Value;

				// Check if there's a significant temperature change
				const temperatureChange = Math.abs(currentValue - lastIncludedValue);

				// Include point ONLY if:
				// 1. Significant temperature change (> threshold)
				// 2. OR it's the last point (to show final value)
				if (
					temperatureChange >= temperatureThreshold ||
					i === probeData.length - 1
				) {
					filteredData.push(currentData);
					lastIncludedValue = currentValue;
				}
			}

			// COMMENTED OUT: Context point logic - decided to keep original behavior
			// Second pass: ensure we have a context point before the heat period
			// This ensures visibility when zoomed-in on the heat period
			// if (eafProcessData?.Start && filteredData.length > 0) {
			// 	const heatStartTime = eafProcessData.Start.getTime();
			//
			// 	// Find the last point before the heat period
			// 	let lastPointBeforeHeat: StrandTimeSeriesData | null = null;
			// 	for (let i = probeData.length - 1; i >= 0; i--) {
			// 		const pointTime = probeData[i].Datetime.getTime();
			// 		if (pointTime < heatStartTime) {
			// 			lastPointBeforeHeat = probeData[i];
			// 			break;
			// 		}
			// 	}
			//
			// 	// If we found a point before heat and it's not already included, add it
			// 	if (lastPointBeforeHeat) {
			// 		const isAlreadyIncluded = filteredData.some(point =>
			// 			Math.abs(point.Datetime.getTime() - lastPointBeforeHeat!.Datetime.getTime()) < (60 * 1000) // Within 1 minute
			// 		);
			//
			// 		if (!isAlreadyIncluded) {
			// 			// Create a context point 5 minutes after the heat start for better positioning
			// 			const contextTime = new Date(heatStartTime + (5 * 60 * 1000)); // 5 minutes after heat start
			// 			const contextPoint: StrandTimeSeriesData = {
			// 				...lastPointBeforeHeat,
			// 				Datetime: contextTime
			// 			};
			//
			// 			// Insert it at the beginning to maintain chronological order
			// 			filteredData.unshift(contextPoint);
			// 		}
			// 	}
			// }

			// Sort by time to maintain chronological order
			filteredData.sort((a, b) => a.Datetime.getTime() - b.Datetime.getTime());

			return filteredData;
		};

		// Calculate initial zoom range for heat period (configurable minutes before/after are available)
		const getInitialZoomRange = () => {
			if (!eafProcessData?.Start) return undefined;

			const heatStart = eafProcessData.Start.getTime();
			const heatEnd = eafProcessData.End?.getTime() || Date.now();

			// Calculate the percentage of the total range that represents the heat period
			// Total range: heatStart - offsetMinutes to heatEnd + offsetMinutes
			const totalStart = heatStart - offsetMinutes * 60 * 1000; // configurable minutes before
			const totalEnd = heatEnd + offsetMinutes * 60 * 1000; // configurable minutes after
			const totalRange = totalEnd - totalStart;
			const heatRange = heatEnd - heatStart;

			// Calculate start and end percentages for zoom
			const startPercent = ((heatStart - totalStart) / totalRange) * 100;
			const endPercent = ((heatEnd - totalStart) / totalRange) * 100;

			return {
				start: Math.max(0, startPercent),
				end: Math.min(100, endPercent),
			};
		};

		const initialZoom = getInitialZoomRange();

		return {
			animation: false,
			toolbox: {
				feature: {
					saveAsImage: {
						name: `Heat ${heatName} - EAF Process Data`,
						pixelRatio: 2,
					},
				},
			},
			dataZoom: [
				{
					type: "inside",
					show: true,
					start: initialZoom?.start || 0,
					end: initialZoom?.end || 100,
				},
			],
			tooltip: {
				trigger: "axis",
				formatter: (params: any) => {
					// Check for specific series to prioritize their information
					const probeTempParam = params.find(
						(param: any) => param.seriesName === "Probe temp",
					);
					const additionParam = params.find(
						(param: any) => param.seriesName === "Additions",
					);

					// Use simplified time format for all tooltips
					const datetime = moment(params[0].value[0]).format("HH:mm:ss");

					if (probeTempParam) {
						// When there's probe temp, show probe temp + power if available
						const powerParam = params.find(
							(param: any) => param.seriesName === "Power",
						);

						let tooltipContent = `Probe temp: ${probeTempParam.value[1].toFixed(1)}°F`;
						if (powerParam) {
							tooltipContent += `<br>Power: ${powerParam.value[1].toFixed(1)} MW`;
						}

						return `Time: ${datetime}<br>${tooltipContent}`;
					} else if (additionParam) {
						// When there's an addition, show addition info + power if available
						const powerParam = params.find(
							(param: any) => param.seriesName === "Power",
						);

						// Find the actual addition data to get material name and quantity
						const addition = additions?.find(
							(a) => a.Timestamp.getTime() === additionParam.value[0],
						);

						let tooltipContent = "";
						if (addition) {
							const materialName = addition.MaterialName || "";
							const weight = Math.floor(addition.Quantity);
							tooltipContent = materialName
								? `${materialName}: ${weight} kg`
								: `Addition: ${weight} kg`;
						} else {
							tooltipContent = "Addition";
						}

						if (powerParam) {
							tooltipContent += `<br>Power: ${powerParam.value[1].toFixed(1)} MW`;
						}

						return `Time: ${datetime}<br>${tooltipContent}`;
					} else {
						// For other series, show all available
						const tooltipContent = params
							.map((param: any) => {
								let unit = "";
								const seriesName = param.seriesName;
								if (seriesName === "Power") unit = " MW";
								else if (seriesName === "KWH/Ton") unit = " kWh/ton";
								return `${seriesName}: ${param.value[1].toFixed(1)}${unit}`;
							})
							.join("<br>");
						return `Time: ${datetime}<br>${tooltipContent}`;
					}
				},
			},
			legend: {
				data: [
					...Object.keys(groupedData)
						.map((tagName) => tagDisplayNames[tagName] || tagName)
						.sort(),
					"Scrap Charges",
					"Additions",
				],
				top: "bottom",
				selectedMode: true,
				textStyle: {
					fontSize: 10,
				},
				itemGap: 20,
			},
			xAxis: {
				type: "time",
				axisLabel: {
					formatter: (value: number) => {
						return moment(value).format("HH:mm");
					},
				},
			},
			yAxis: [
				// Y-axis 0: KWH_Per_Ton
				{
					type: "value",
					name: "kWh/ton",
					nameTextStyle: {
						fontSize: 12,
						color: strandColors.KWH_Per_Ton,
						align: "left",
					},
					nameLocation: "end",
					position: "left",
					axisLabel: {
						color: strandColors.KWH_Per_Ton,
					},
					axisLine: {
						lineStyle: {
							color: strandColors.KWH_Per_Ton,
						},
					},
					...getAxisLimits("KWH_Per_Ton"),
				},
				// Y-axis 1: Power_MW
				{
					type: "value",
					name: "MW",
					nameTextStyle: {
						fontSize: 12,
						color: strandColors.Power_MW,
						align: "right",
					},
					nameLocation: "end",
					position: "right",
					axisLabel: {
						color: strandColors.Power_MW,
					},
					axisLine: {
						lineStyle: {
							color: strandColors.Power_MW,
						},
					},
					...getAxisLimits("Power_MW"),
				},
				// Y-axis 2: Steel_Temp
				{
					type: "value",
					name: "°F",
					nameTextStyle: {
						fontSize: 12,
						color: strandColors.Steel_Temp,
						align: "left",
					},
					nameLocation: "end",
					position: "left",
					offset: 80,
					axisLabel: {
						color: strandColors.Steel_Temp,
					},
					axisLine: {
						lineStyle: {
							color: strandColors.Steel_Temp,
						},
					},
					...getAxisLimits("Steel_Temp"),
				},
			],
			series: [
				// Existing EAF data series
				...Object.entries(groupedData).map(([tagName, tagData]) => {
					const displayName = tagDisplayNames[tagName] || tagName;
					const isProbeTemp = tagName === "Steel_Temp";

					if (isProbeTemp) {
						// Filter Probe temp to show only significant changes as events
						const filteredProbeData = filterProbeTempEvents(tagData);

						return {
							name: displayName,
							type: "scatter",
							yAxisIndex: tagAxisMapping[tagName] || 0,
							symbol: "circle",
							symbolSize: 10,
							itemStyle: {
								color: strandColors[tagName] || COLORS.GREEN_BLUE,
								borderColor: "#fff",
								borderWidth: 2,
							},
							label: {
								show: false,
							},
							emphasis: {
								itemStyle: {
									shadowBlur: 15,
									shadowColor: strandColors[tagName] || COLORS.GREEN_BLUE,
								},
								label: {
									show: true,
									position: "top",
									formatter: (params: any) => {
										return `${params.value[1].toFixed(0)}°F`;
									},
									fontSize: 11,
									fontWeight: "bold",
									color: strandColors[tagName] || COLORS.GREEN_BLUE,
								},
							},
							data: filteredProbeData.map((item) => [
								item.Datetime.getTime(),
								mapTemperatureToBand(item.Value),
							]),
						};
					} else {
						// Regular line series for KWH/Ton and Power
						return {
							name: displayName,
							type: "line",
							smooth: true,
							symbol: "none",
							sampling: "lttb",
							yAxisIndex: tagAxisMapping[tagName] || 0,
							itemStyle: {
								color: strandColors[tagName] || COLORS.GREEN_BLUE,
							},
							lineStyle: {
								width: 1.5,
							},
							data: tagData.map((item) => [
								item.Datetime.getTime(),
								item.Value,
							]),
						};
					}
				}),
				// Scrap Charges series
				{
					name: "Scrap Charges",
					type: "scatter",
					yAxisIndex: 2, // Use temperature y-axis for band positioning
					symbol: "triangle",
					symbolSize: 12,
					itemStyle: {
						color: COLORS.AMARANTH_RED, // Red color for scrap charges
						borderColor: "#fff",
						borderWidth: 2,
					},
					label: {
						show: true,
						position: "top",
						formatter: (params: any) => {
							const charge = charges?.find(
								(c) => c.Timestamp.getTime() === params.value[0],
							);
							if (charge) {
								const bucketName = charge.BucketName || "";
								return bucketName
									? `${bucketName}\n${charge.Weight} lbs`
									: `${charge.Weight} lbs`;
							}
							return "";
						},
						fontSize: 10,
						fontWeight: "bold",
						color: COLORS.AMARANTH_RED,
						backgroundColor: "rgba(255, 255, 255, 0.8)",
						borderColor: COLORS.AMARANTH_RED,
						borderWidth: 1,
						borderRadius: 3,
						padding: [2, 4],
					},
					emphasis: {
						itemStyle: {
							shadowBlur: 15,
							shadowColor: COLORS.AMARANTH_RED,
						},
					},
					data: (charges || [])
						.filter((charge) => charge.ChargeCode === "B") // Only bucket charges
						.map((charge, index, array) => [
							charge.Timestamp.getTime(),
							mapScrapChargeToBand(index, array.length),
						]), // Y position distributed in 0-10% band with alternating min/max
				},
				// Additions series
				{
					name: "Additions",
					type: "scatter",
					yAxisIndex: 2, // Use temperature y-axis for band positioning
					symbol: "diamond",
					symbolSize: 10,
					itemStyle: {
						color: COLORS.WAGENINGEN_GREEN, // Green color for additions
						borderColor: "#fff",
						borderWidth: 2,
					},
					label: {
						show: false,
					},
					emphasis: {
						itemStyle: {
							shadowBlur: 15,
							shadowColor: COLORS.WAGENINGEN_GREEN,
						},
						label: {
							show: true,
							position: "top",
							formatter: (params: any) => {
								const addition = additions?.find(
									(a) => a.Timestamp.getTime() === params.value[0],
								);
								if (addition) {
									const materialName = addition.MaterialName || "";
									const weight = Math.floor(addition.Quantity);
									return materialName
										? `${materialName}\n${weight} kg`
										: `${weight} kg`;
								}
								return "";
							},
							fontSize: 9,
							fontWeight: "bold",
							color: COLORS.WAGENINGEN_GREEN,
							backgroundColor: "rgba(255, 255, 255, 0.8)",
							borderColor: COLORS.WAGENINGEN_GREEN,
							borderWidth: 1,
							borderRadius: 3,
							padding: [2, 4],
						},
					},
					data: (additions || []).map((addition, index, array) => [
						addition.Timestamp.getTime(),
						mapAdditionToBand(index, array.length),
					]), // Y position distributed in 10-60% band
				},
			],
		};
	}, [eafValues, heatName, eafProcessData, charges, additions, offsetMinutes]);

	return chartOptions;
};
